import { useState, useEffect, useCallback } from 'react';
import { getValidToken } from '../utils/auth';
import TradingDatePicker from './TradingDatePicker';
import { etTodayStr } from '../utils/tradingCalendar';
import { optionTradingViewUrl } from '../utils/tradingViewLink';

const API_URL = process.env.REACT_APP_API_URL || '';

const STATUS_COLOR = { Active: '#60a5fa', Success: '#22c55e', Failed: '#ef4444' };
const BIAS_COLOR = { bullish: '#22c55e', bearish: '#ef4444', neutral: '#9ca3af' };
const PATTERN_LABEL = { pullback: 'Dip', trend: 'Runner' };

function formatTime(unixMs) {
  if (!unixMs) return '—';
  return new Date(unixMs).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York',
  });
}

// % change from the buy price. Failed: if the trailing stop had triggered
// (ts set), use ts - that's the actual level the exit locked in, since the
// realized low can overshoot it (e.g. a bad quote tick) and misreport a
// trail-stop win as a loss. No trail: use low. Every other status uses the
// high (the peak reached so far / at TP).
function pctFromBuy(entry, high, low, status, ts) {
  if (!entry) return null;
  const ref = status === 'Failed' ? (ts > 0 ? ts : low) : high;
  return ((ref - entry) / entry) * 100;
}

function PctCell({ entry, high, low, status, ts }) {
  const pct = pctFromBuy(entry, high, low, status, ts);
  if (pct == null) return <td className="py-1.5 pr-3 text-right font-mono text-gray-500">—</td>;
  const color = pct >= 0 ? '#22c55e' : '#ef4444';
  return (
    <td className="py-1.5 pr-3 text-right font-mono font-semibold" style={{ color }}>
      {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
    </td>
  );
}

function openTradingView(symbol) {
  const url = optionTradingViewUrl(symbol);
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

// New discrete-trigger SPX signal engine (backend: handlers/smart_entry.go),
// built to replace the continuous composite-score ranking used by
// BestContractsWidget. Runs side by side with the old widget until it proves
// out over a multi-week comparison — see the "Smart Entry" plan.
//
// Universe = top-20 SPX/SPXW contracts by volume (with rank hysteresis so the
// naturally-rotating leaderboard doesn't thrash). A signal only fires when the
// SPY/SPX underlying trend (RSI/MACD/EMA) confirms direction AND the
// contract's own price shows a pullback-resumption or sustained-higher-lows
// pattern AND a Greeks/liquidity gate passes — one active pick per side.
//
// apiPath/title/subtitle let the same component render both the original
// engine and the delta-consistency-filtered variant (a separate backend
// instance tagged with variant="deltacheck") so the two can be compared
// side by side on live data.
export default function SmartEntryWidget({
  apiPath = '/api/smart-signals',
  title = 'Smart Entry (SPX)',
  subtitle = 'Top-20-by-volume watchlist · underlying trend confirmation · pullback/runner entry timing · Greeks final pick',
}) {
  const [date, setDate] = useState(() => etTodayStr());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (d) => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${API_URL}${apiPath}?date=${d}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [apiPath]);

  useEffect(() => {
    fetchData(date);
    const timer = setInterval(() => fetchData(date), 5000);
    return () => clearInterval(timer);
  }, [date, fetchData]);

  const isToday = date === etTodayStr();
  const bias = data?.bias || 'neutral';
  const signals = data?.signals || [];
  const watchlist = data?.watchlist || [];
  const counts = { Active: 0, Success: 0, Failed: 0 };
  for (const s of signals) if (counts[s.status] != null) counts[s.status]++;

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          {title}
        </span>
        <span className="text-xs px-2 py-1 rounded font-semibold" style={{ backgroundColor: (BIAS_COLOR[bias] || '#9ca3af') + '22', color: BIAS_COLOR[bias] || '#9ca3af' }}>
          {bias.charAt(0).toUpperCase() + bias.slice(1)}
        </span>
        {data && (
          <span className="text-xs text-gray-500 font-mono">
            RSI {data.rsi?.toFixed(1)} · MACD {data.macd_hist >= 0 ? '+' : ''}{data.macd_hist?.toFixed(3)} · EMA9 {data.ema9?.toFixed(2)} / EMA20 {data.ema20?.toFixed(2)}
          </span>
        )}
        <div className="flex-1" />
        {Object.entries(counts).map(([st, n]) => (
          <span key={st} className="text-xs font-semibold" style={{ color: STATUS_COLOR[st] }}>{st} {n}</span>
        ))}
        <TradingDatePicker value={date} onChange={setDate} />
      </div>
      <div className="text-xs text-gray-500 mb-3">
        {subtitle}
      </div>
      {error && <div className="text-red-400 text-sm mb-3">Failed to load: {error}</div>}

      {isToday && (
        <div className="overflow-x-auto mb-3">
          <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
                {['Rank', 'Symbol', 'Type', 'Strike', 'Price', 'Volume', 'Delta', 'Pattern'].map((h) => (
                  <th key={h} className="text-left py-2 pr-3 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {watchlist.length === 0 ? (
                <tr><td colSpan={8} className="py-4 text-center text-gray-600">No candidates yet</td></tr>
              ) : watchlist.map((w) => (
                <tr key={w.symbol} onClick={() => openTradingView(w.symbol)}
                  className="border-b cursor-pointer hover:bg-gray-800/50" style={{ borderColor: '#1f2937' }}>
                  <td className="py-1.5 pr-3 text-gray-500">{w.rank}</td>
                  <td className="py-1.5 pr-3 font-semibold text-gray-200">{w.symbol}</td>
                  <td className="py-1.5 pr-3 text-gray-400">{w.type}</td>
                  <td className="py-1.5 pr-3 text-gray-400 font-mono">{w.strike}</td>
                  <td className="py-1.5 pr-3 text-gray-400 font-mono">${w.price.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-gray-400 font-mono">{(w.volume / 1000).toFixed(0)}K</td>
                  <td className="py-1.5 pr-3 text-gray-400 font-mono">{w.delta.toFixed(2)}</td>
                  <td className="py-1.5 pr-3">
                    {w.pattern && <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                      style={{ backgroundColor: '#60a5fa22', color: '#60a5fa' }}>{PATTERN_LABEL[w.pattern] || w.pattern}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
              {['Symbol', 'Entry', 'SL', 'TS', 'TP', 'H', 'L', 'Delta', 'Entry Time', 'Exit Time', '%', 'Status'].map((h) => (
                <th key={h} className={`py-2 pr-3 font-medium ${h === 'Symbol' ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {signals.length === 0 ? (
              <tr><td colSpan={12} className="py-4 text-center text-gray-600">No signals</td></tr>
            ) : signals.map((s, i) => (
              <tr key={`${s.symbol}-${s.added_at}-${i}`} onClick={() => openTradingView(s.symbol)}
                className="border-b cursor-pointer hover:bg-gray-800/50" style={{ borderColor: '#1f2937' }}>
                <td className="py-1.5 pr-3 font-semibold text-gray-200">{s.symbol}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-300">${s.entry.toFixed(2)}</td>
                <td className="py-1.5 pr-3 text-right font-mono" style={{ color: '#ef4444' }}>${s.sl.toFixed(2)}</td>
                <td className="py-1.5 pr-3 text-right font-mono" style={{ color: s.ts > 0 ? '#f59e0b' : '#4b5563' }}>
                  {s.ts > 0 ? `$${s.ts.toFixed(2)}` : '—'}
                </td>
                <td className="py-1.5 pr-3 text-right font-mono" style={{ color: '#22c55e' }}>${s.tp.toFixed(2)}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-400">${s.high.toFixed(2)}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-400">${s.low.toFixed(2)}</td>
                <td className="py-1.5 pr-3 text-right font-mono text-gray-400">{s.delta.toFixed(2)}</td>
                <td className="py-1.5 pr-3 text-right text-gray-400">{formatTime(s.added_at)}</td>
                <td className="py-1.5 pr-3 text-right text-gray-400">{formatTime(s.resolved_at)}</td>
                <PctCell entry={s.entry} high={s.high} low={s.low} status={s.status} ts={s.ts} />
                <td className="py-1.5 pr-3 text-right">
                  <span className="font-semibold px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: (STATUS_COLOR[s.status] || '#6b7280') + '22', color: STATUS_COLOR[s.status] || '#6b7280' }}>
                    {s.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
